"use client";

import { MultiSelectDropdown } from "@/components/ui";
import { LEADERBOARD_SEARCH_API, countryISOs } from "@/lib/constants";
import { cn, debounce, formatImageUrl } from "@/lib/utils";
import { TLeaderBoardData } from "@/types/leaderboard";
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronUpIcon,
	FilterIcon,
	SearchIcon,
} from "lucide-react";
import Image from "next/image";
import { Dispatch, SetStateAction, useRef, useState } from "react";

type LeaderBoardActionBarProps = {
	initialResults: TLeaderBoardData["leaderboardUsers"];
	parentCategories: {
		id: string;
		name: string;
		slug: string;
		icon: string | null;
	}[];
	positions: (string | null)[];
	setSearchResults: Dispatch<SetStateAction<TLeaderBoardData["leaderboardUsers"] | undefined>>;
	setIsFetching: Dispatch<SetStateAction<boolean>>;
};

const LeaderBoardActionBar = ({
	initialResults,
	parentCategories,
	positions,
	setSearchResults,
	setIsFetching,
}: LeaderBoardActionBarProps) => {
	const searchRef = useRef<HTMLInputElement | null>(null);
	const [toggleFilterSection, setToggleFilterSection] = useState<boolean>(false);
	const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
	const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
	const [showLeftScrollButton, setShowLeftScrollButton] = useState<boolean>(false);
	const [searchQuery, setSearchQuery] = useState<string>("");

	const [selectedCategory, setSelectedCategory] = useState<string>("all");

	const positionDropDownItems = positions.map((position) => {
		return {
			id: position || "",
			name: position || "",
		};
	});

	const locationsDropDownItems = Object.entries(countryISOs).map(([countryName, countryISO]) => ({
		id: countryISO,
		name: countryName,
	}));

	const getLeaderSearchResults = async ({
		searchQuery = "",
		selectedLocations = [],
		selectedPositions = [],
		selectedCategory = "all",
		pageParam = 1,
		perPageParam = 500,
	}: {
		searchQuery?: string;
		selectedLocations: string[];
		selectedPositions: string[];
		selectedCategory?: string;
		pageParam?: number;
		perPageParam?: number;
	}) => {
		try {
			if (
				searchQuery === "" &&
				selectedLocations.length === 0 &&
				selectedPositions.length === 0 &&
				selectedCategory === "all"
			) {
				setSearchResults(initialResults);
				return;
			}

			setIsFetching(true);
			const url = new URL(LEADERBOARD_SEARCH_API);

			url.searchParams.set("query", searchQuery);
			url.searchParams.set("locations", selectedLocations.join(","));
			url.searchParams.set("positions", selectedPositions.join(","));
			url.searchParams.set("category", selectedCategory);
			url.searchParams.set("page", pageParam.toString());
			url.searchParams.set("perPage", perPageParam.toString());

			const res = await fetch(url);
			if (!res.ok) {
				const { message } = await res.json();
				throw new Error(message);
			}

			const { data } = (await res.json()) as { data: TLeaderBoardData["leaderboardUsers"] };

			if (!data) {
				setSearchResults([]);
			}

			// NOTE: Temporary implementation to satisfy the requirements
			if (selectedCategory === "buy-sell-transactions") {
				data.sort((a, b) => {
					// Function to compare two users based on their names
					const nameA = a.name || "";
					const nameB = b.name || "";

					// Check if either name matches "James P. Schlimmer"
					if (nameA === "James P. Schlimmer") return -1;
					if (nameB === "James P. Schlimmer") return 1;

					return nameA.localeCompare(nameB);
				});
			}

			setSearchResults(data);
		} catch (error) {
			console.log("Error occured during search");
		} finally {
			setIsFetching(false);
		}
	};

	const handleLocationFilter = debounce((locations: string[]) => {
		setSelectedLocations(locations);
		getLeaderSearchResults({
			searchQuery,
			selectedLocations: locations,
			selectedPositions,
			selectedCategory,
		});
	}, 300);

	const handlePositionFilter = debounce((positions: string[]) => {
		setSelectedPositions(positions);
		getLeaderSearchResults({
			searchQuery,
			selectedLocations,
			selectedPositions: positions,
			selectedCategory,
		});
	}, 300);

	const handleCategoryFilter = debounce((categorySlug: string) => {
		setSelectedCategory(categorySlug);
		getLeaderSearchResults({
			searchQuery,
			selectedLocations,
			selectedPositions,
			selectedCategory: categorySlug,
		});
	}, 300);

	const handleSearch = debounce((query: string) => {
		const searchOptions = {
			searchQuery: "",
			selectedLocations: selectedLocations.length > 0 ? selectedLocations : [],
			selectedPositions: selectedPositions.length > 0 ? selectedPositions : [],
			selectedCategory: selectedCategory ? selectedCategory : "all",
		};

		if (query.trim() !== "") {
			searchOptions["searchQuery"] = query;
		}

		setSearchQuery(query);
		getLeaderSearchResults(searchOptions);
	}, 300);

	const handleResetFilters = () => {
		if (searchRef.current?.value) {
			searchRef.current.value = "";
		}

		setSearchQuery("");
		setSelectedLocations([]);
		setSelectedPositions([]);
		setSearchResults(initialResults);
	};

	const handleScrollClick = (direction: "left" | "right") => {
		const container = document.querySelector("#categoriesList");

		if (container) {
			const scrollAmount = 1080;

			direction === "left"
				? (container.scrollLeft -= scrollAmount)
				: (container.scrollLeft += scrollAmount);

			setShowLeftScrollButton(container.scrollLeft > 0 ? true : false);
		}
	};

	return (
		<div className="mx-auto mt-32 w-full max-w-6xl">
			<div className="rounded-lg bg-gradient-to-br from-white to-brand-primary/5 p-6 shadow-sm">
				<div className="flex items-center justify-between gap-4">
					<div className="flex items-center gap-4 overflow-x-auto">
						<span className="font-medium">Categories:</span>

						{showLeftScrollButton ? (
							<button onClick={() => handleScrollClick("left")}>
								<ChevronLeftIcon className="h-6 w-6 text-brand-primary" />
							</button>
						) : null}

						<div
							className="no-scrollbar flex items-center justify-start gap-4 overflow-x-scroll"
							id="categoriesList"
						>
							<button
								className={cn(
									"flex items-center gap-1 rounded-lg  p-2",
									selectedCategory === "all"
										? "bg-brand-primary text-white"
										: "bg-transparent text-gray-600"
								)}
								onClick={() => handleCategoryFilter("all")}
							>
								All
							</button>

							{parentCategories.map((category) => (
								<button
									key={category.name}
									className={cn(
										"flex items-center gap-1 rounded-lg bg-transparent p-2 text-gray-400",
										selectedCategory === category.slug
											? "bg-brand-primary text-white"
											: "bg-transparent text-gray-600"
									)}
									onClick={() => handleCategoryFilter(category.slug)}
								>
									<div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded">
										<Image
											className={cn(
												"h-full w-full object-contain p-1",
												selectedCategory === category.slug
													? "bg-white text-brand-primary"
													: "bg-transparent text-gray-600"
											)}
											src={formatImageUrl(category.icon) || ""}
											alt={category.name}
											width={12}
											height={12}
										/>
									</div>
									<span className="whitespace-nowrap break-keep text-sm">{category.name}</span>
								</button>
							))}
						</div>
					</div>

					<button onClick={() => handleScrollClick("right")}>
						<ChevronRightIcon className="h-6 w-6 text-brand-primary" />
					</button>
				</div>
			</div>

			<div className="relative z-10 -mt-4">
				<div className="flex items-center justify-end">
					<button
						onClick={() => setToggleFilterSection((prev) => (prev = !prev))}
						className="mr-8 flex h-8 w-8 items-center justify-center rounded-lg bg-white p-1 text-brand-primary shadow-lg"
					>
						{toggleFilterSection ? (
							<ChevronUpIcon className="h-5 w-5" />
						) : (
							<FilterIcon className="h-5 w-5" />
						)}
					</button>
				</div>
			</div>

			{toggleFilterSection ? (
				<div className="relative z-20 -mt-4 rounded bg-white p-6 shadow-sm">
					<div className="flex flex-col gap-4 md:flex-row">
						<div className="flex flex-1 items-center justify-start gap-4 lg:flex-none">
							<label htmlFor="category" className="whitespace-nowrap text-sm text-dark-secondary">
								Location:
							</label>

							<div className="w-full lg:w-40">
								<MultiSelectDropdown
									items={locationsDropDownItems}
									selectedItems={selectedLocations}
									setActiveItems={handleLocationFilter}
									buttonLabel="All Status"
								/>
							</div>
						</div>

						<div className="flex flex-1 items-center justify-start gap-4 lg:flex-none">
							<label htmlFor="category" className="whitespace-nowrap text-sm text-dark-secondary">
								Position:
							</label>

							<div className="w-full lg:w-40">
								<MultiSelectDropdown
									items={positionDropDownItems}
									selectedItems={selectedPositions}
									setActiveItems={handlePositionFilter}
									buttonLabel="All Status"
								/>
							</div>
						</div>

						<div
							className={cn(
								"group flex min-w-max flex-1 items-center rounded border border-gray-200 bg-white px-4 py-2",
								"transition-colors duration-150 ease-linear focus-within:border-brand-primary"
							)}
						>
							<span>
								<SearchIcon className="h-4 w-4 text-gray-400 transition-colors duration-150 ease-linear group-focus-within:text-blue-500" />
							</span>

							<input
								ref={searchRef}
								id="search"
								type="search"
								name="search"
								placeholder="Search"
								className="w-full px-2 text-sm focus:outline-none"
								onChange={(e) => handleSearch(e.target.value)}
							/>
						</div>
						<button
							onClick={handleResetFilters}
							className="rounded-lg border border-brand-primary px-6 py-2 text-sm text-brand-primary"
						>
							Reset
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default LeaderBoardActionBar;
